/**
 * The nest's words and orderings: pure, so the Nest tab, the poster line and the invite
 * landing stay thin and these stay tested (nests/model.test.ts). The api ranks each level
 * (clean solves by time, then solves with a reveal by time, ties sharing); this module
 * joins its `memberId`s to names and decides how a person's row reads.
 */
import { addDays, type DateKey, formatDuration, localDateKey, weekday } from '@arrow/game-engine';
import type { Level } from '@arrow/puzzle-schema';

import { SITE_URL } from '../env.ts';
import type { ActivityItem, BoardState, FeatherBoard, NestMember, NestView, TodayBoard, TodayEntry, WeekDay } from './api.ts';

export const LEVEL_LABEL: Record<Level, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

/** The words a nudge carries, keyed as the api stores them (the prototype's set phrases). */
export const NUDGE_WORDS: Record<string, string> = {
	your_go: 'Your go',
	easy_quick: 'The Easy’s a quick one',
	good_luck: 'Good luck',
};

/** "1st", "2nd", "3rd", "4th", "11th", "22nd". */
export function ordinal(n: number): string {
	const t = n % 100;
	if (t >= 11 && t <= 13) return `${n}th`;
	return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/** The first word of a name ("Grandad Pete" → "Grandad"), as the invite page and nudges say it. */
export function firstName(name: string): string {
	return name.trim().split(/\s+/)[0] ?? name;
}

/**
 * How a nudge or a toast names someone: the first word when nobody else in the nest shares
 * it ("Grandad"), else the whole name ("Jo A"), so it never names the wrong person (NJ-18).
 * `among` is everyone's display name in the nest.
 */
export function shortName(name: string, among: readonly string[]): string {
	const first = firstName(name);
	const key = first.toLocaleLowerCase('en-GB');
	const clash = among.some((other) => other.trim() !== name.trim() && firstName(other).toLocaleLowerCase('en-GB') === key);
	return clash ? name.trim() : first;
}

export type NameOf = (memberId: string) => string;

/** Names from the member list; the viewer is "You". A member who has gone reads as "Someone". */
export function namesFor(members: readonly NestMember[], me: string): NameOf {
	const byId = new Map(members.map((m) => [m.memberId, m.displayName]));
	return (id) => (id === me ? 'You' : (byId.get(id) ?? 'Someone'));
}

// ------------------------------------------------------------------------- today's times

const LEVEL_ORDER: readonly Level[] = ['easy', 'medium', 'hard'];
const TIMED: ReadonlySet<BoardState> = new Set(['solved', 'help']);
const FINISHED: ReadonlySet<BoardState> = new Set(['solved', 'help', 'late', 'unverified']);

/** "1 reveal", "7 reveals". */
export function revealsWords(n: number): string {
	return n === 1 ? '1 reveal' : `${n} reveals`;
}

/** What a solve's reveal count reads as: "2 reveals", "Reveals" from an api that hasn't sent the count, or null for none. */
function revealsLabel(state: BoardState, reveals: number | null): string | null {
	if (state !== 'help') return null;
	return reveals === null ? 'Reveals' : revealsWords(Math.max(1, reveals));
}

/**
 * Who leads a level: the entry the api ranks 1st (clean times first, then times with reveals
 * by fewest reveals, then by time: the feathers' order). A tie at 1st goes to the name here.
 */
function leader(entries: readonly TodayEntry[], nameOf: NameOf): TodayEntry | null {
	let best: TodayEntry | null = null;
	for (const e of entries) {
		if (e.rank !== 1 || !TIMED.has(e.state) || e.durationMs === null) continue;
		if (!best || nameOf(e.memberId).localeCompare(nameOf(best.memberId), 'en-GB') < 0) best = e;
	}
	return best;
}

/** A level card over Today's list: the level and its leading time (the api's 1st), and whose. */
export interface LevelCard {
	level: Level;
	label: string;
	leader: { name: string; time: string } | null;
}

export function levelCards(board: TodayBoard, members: readonly NestMember[]): LevelCard[] {
	const nameOf = namesFor(members, board.me);
	return board.levels.map(({ level, entries }) => {
		const q = leader(entries, nameOf);
		return { level, label: LEVEL_LABEL[level], leader: q ? { name: nameOf(q.memberId), time: formatDuration(q.durationMs!) } : null };
	});
}

/**
 * The level Today opens on: the last level you finished today (`remembered`, the level you
 * last played, if you've finished it; else the hardest you've finished), else Easy, else the
 * day's first. Finished means the board has your finish, or this device has (`doneHere`).
 */
export function openingLevel(board: TodayBoard, remembered: Level | null, doneHere: (level: Level) => boolean = () => false): Level {
	const levels = board.levels.map((l) => l.level);
	const done = levels.filter((level) => {
		const mine = board.levels.find((l) => l.level === level)?.entries.find((e) => e.memberId === board.me);
		return (mine && FINISHED.has(mine.state)) || doneHere(level);
	});
	if (remembered && done.includes(remembered)) return remembered;
	const last = LEVEL_ORDER.filter((l) => done.includes(l)).at(-1);
	return last ?? (levels.includes('easy') ? 'easy' : (levels[0] ?? 'easy'));
}

/** One person on one level today. */
export interface LevelRow {
	memberId: string;
	name: string;
	me: boolean;
	state: BoardState;
	/** Place among everyone with a time: the api's rank (ties share). Null without a time. */
	place: number | null;
	time: string | null;
	/** Ranked 1st at this level (ties too), with or without reveals: ticked. */
	first: boolean;
	/** "2 reveals" under the time, or null. */
	reveals: string | null;
	/** The status in place of a time: "Playing", "Not played", "Solved late" (after the day ended), "Solved offline" (can't be timed fairly: NJ-7). */
	status: string | null;
	/** Your own row while you haven't started this level: on top, with "Play {Level}". */
	play: boolean;
	/** Nothing played today at all (and not you): the Nudge button. */
	nudge: boolean;
}

const STATUS: Partial<Record<BoardState, string>> = { playing: 'Playing', none: 'Not played', late: 'Solved late', unverified: 'Solved offline' };

/**
 * One level's list, Today (Nest boards v2):
 *
 * 0. Your own row while you haven't started this level, on top (it carries "Play {Level}").
 * 1. Everyone with a time, in the api's rank (clean times first, then times with reveals by
 *    fewest reveals, then by time), then by name; ties share a place. Rank 1 gets the tick.
 * 2. Finished with no time (late, started offline), by name.
 * 3. Playing, by name.
 * 4. Not played, by name; those who've played nothing today can be nudged.
 *
 * Your row otherwise sits where it falls, highlighted in place.
 */
export function levelRows(board: TodayBoard, members: readonly NestMember[], level: Level): LevelRow[] {
	const nameOf = namesFor(members, board.me);
	const entries = board.levels.find((l) => l.level === level)?.entries ?? [];
	const ids = new Set<string>(members.map((m) => m.memberId));
	for (const l of board.levels) for (const e of l.entries) ids.add(e.memberId);
	const playedToday = (id: string) => board.levels.some((l) => l.entries.some((e) => e.memberId === id && e.state !== 'none'));
	const rows = [...ids].map((memberId): LevelRow => {
		const e = entries.find((x) => x.memberId === memberId);
		const state: BoardState = e?.state ?? 'none';
		const ms = e && TIMED.has(state) ? e.durationMs : null;
		const me = memberId === board.me;
		return {
			memberId,
			name: nameOf(memberId),
			me,
			state,
			place: ms === null ? null : (e?.rank ?? null),
			time: ms === null ? null : formatDuration(ms),
			first: ms !== null && e?.rank === 1,
			reveals: ms === null ? null : revealsLabel(state, e?.reveals ?? null),
			status: ms === null ? (STATUS[state] ?? null) : null,
			play: me && state === 'none',
			nudge: !me && !playedToday(memberId),
		};
	});
	// A time the api left unranked (it shouldn't) follows the ranked ones, as a time with no place.
	const tier = (r: LevelRow) => (r.play ? 0 : r.place !== null ? 1 : r.time !== null ? 2 : r.state === 'late' || r.state === 'unverified' ? 3 : r.state === 'playing' ? 4 : 5);
	return rows.sort((a, b) => tier(a) - tier(b) || (tier(a) === 1 ? a.place! - b.place! : 0) || a.name.localeCompare(b.name, 'en-GB'));
}

/** What a row says to a screen reader: "1st, Diego, 3:18", "2nd, Sam, 9:40, 7 reveals", "Ed, not played". */
export function levelRowSpoken(row: LevelRow): string {
	// The tick marks 1st, which the place already says.
	if (row.time !== null) return [row.place !== null ? `${ordinal(row.place)}, ${row.name}, ${row.time}` : `${row.name}, ${row.time}`, row.reveals].filter(Boolean).join(', ');
	return `${row.name}, ${(row.status ?? 'not played').toLowerCase()}`;
}

// ------------------------------------------------------------------- feathers boards

export interface FeatherRow {
	memberId: string;
	name: string;
	me: boolean;
	rank: number;
	feathers: number;
	/** "3 reveals", only when there were any. */
	reveals: string | null;
	/** "9 days" (All time), from an api that sends it. */
	days: string | null;
	/** The week strip (This week), from an api that sends it. */
	week: WeekDay[] | null;
	/** No solves yet and joined in the last 7 days. */
	isNew: boolean;
}

/**
 * This week / All time: as the api ranks them (most feathers, ties share, then by name).
 * Your row stays where it ranks (v2: highlighted in place, not pinned first).
 */
export function featherRows(board: FeatherBoard, members: readonly NestMember[], todayKey?: DateKey): FeatherRow[] {
	const nameOf = namesFor(members, board.me);
	const since = new Map(members.map((m) => [m.memberId, m.sinceDate]));
	let weekAgo: DateKey | null = null;
	try {
		weekAgo = todayKey ? addDays(todayKey, -6) : null;
	} catch {
		weekAgo = null;
	}
	return [...board.entries]
		.sort((a, b) => a.rank - b.rank || nameOf(a.memberId).localeCompare(nameOf(b.memberId), 'en-GB'))
		.map((e) => {
			const joined = since.get(e.memberId);
			const played = (e.days ?? e.solves) > 0;
			return {
				memberId: e.memberId,
				name: nameOf(e.memberId),
				me: e.memberId === board.me,
				rank: e.rank,
				feathers: e.feathers,
				reveals: e.reveals ? revealsWords(e.reveals) : null,
				days: e.days != null && e.days > 0 ? (e.days === 1 ? '1 day' : `${e.days} days`) : null,
				week: e.week && e.week.length > 0 ? e.week : null,
				isNew: !played && !!joined && !!weekAgo && joined >= weekAgo,
			};
		});
}

/** The strip's words for a screen reader: "4 days played, 1 with reveals". */
export function weekSpoken(week: readonly WeekDay[]): string {
	const played = week.filter((d) => d.state !== 'none').length;
	const rv = week.filter((d) => d.state === 'reveals').length;
	const days = played === 1 ? '1 day played' : `${played} days played`;
	return rv > 0 ? `${days}, ${rv} with reveals` : days;
}

/** The tag on This week's header: "Ends Sun", or "Ends today" on the Sunday. */
export function weekEndsTag(todayKey: DateKey): string {
	try {
		return weekday(todayKey) === 6 ? 'Ends today' : 'Ends Sun';
	} catch {
		return 'Ends Sun';
	}
}

// ---------------------------------------------------------------------------- activity

/** "Easy, Medium, Hard": the levels in the day's order. */
export function levelsLabel(levels: readonly Level[]): string {
	return LEVEL_ORDER.filter((l) => levels.includes(l))
		.map((l) => LEVEL_LABEL[l])
		.join(', ');
}

/**
 * One feed item as a label (v2): who, then what, never a sentence.
 * "Diego" · "Easy, Medium, Hard"; "Ed" · "Joined"; "You" · "Nudged Ed";
 * "Mum" · "Nudged you · “Your go”". A solve's reveals are on the list above, not here.
 */
export function activityLabel(item: ActivityItem, nameOf: NameOf, me: string): { who: string; what: string } {
	const who = nameOf(item.memberId);
	switch (item.kind) {
		case 'join':
			return { who, what: 'Joined' };
		case 'solve': {
			const done = item.levels.filter((l) => FINISHED.has(l.state));
			return { who, what: done.length ? levelsLabel(done.map((l) => l.level)) : 'Solved' };
		}
		case 'nudge': {
			if (item.memberId === me) return { who, what: `Nudged ${item.targetMemberId ? nameOf(item.targetMemberId) : 'someone'}` };
			const words = item.phrase ? NUDGE_WORDS[item.phrase] : undefined;
			return { who, what: words ? `Nudged you · “${words}”` : 'Nudged you' };
		}
	}
}

/** An item's time of day on this device: "18:42". Empty when the api sent none. */
export function activityTime(at: string): string {
	if (!at) return '';
	const d = new Date(at);
	if (Number.isNaN(d.getTime())) return '';
	return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** "Well done" is for other people's solves (the api refuses your own; joins and nudges aren't a feat). */
export function canSayWellDone(item: ActivityItem, me: string): boolean {
	return item.memberId !== me && item.kind === 'solve';
}

/**
 * Activity is today's: solves and nudges on today's daily, joins on the device's date
 * (earlier days would read the same twice).
 */
export function todaysActivity(items: readonly ActivityItem[], number: number, todayKey: string): ActivityItem[] {
	return items.filter((i) => (i.kind === 'join' ? !!i.at && localDateKey(new Date(i.at)) === todayKey : i.number === number));
}

/** Members you've nudged on daily `number` (from the feed: a nudge is only visible to its sender and target). */
export function nudgedOn(items: readonly ActivityItem[], number: number, me: string): Set<string> {
	const out = new Set<string>();
	for (const i of items) if (i.kind === 'nudge' && i.memberId === me && i.number === number && i.targetMemberId) out.add(i.targetMemberId);
	return out;
}

/** The toast after a nudge: "Nudge sent. Grandad will see “Jo: your go”." (`among`: everyone's name in the nest, for {@link shortName}.) */
export function nudgeSentLine(target: string, myName: string, among: readonly string[] = []): string {
	return `Nudge sent. ${shortName(target, among)} will see “${shortName(myName, among)}: your go”.`;
}

// ------------------------------------------------------------------- the poster's line

/**
 * "2nd of 3 in Family today": where your time sits on today's board at `level`, or null
 * while you have no time there yet (not synced, late, unverified). Counts everyone with a
 * time, clean or not; a tie says "Joint".
 */
export function placeLine(board: TodayBoard, level: Level, nestName: string): { text: string; spoken: string } | null {
	const entries = board.levels.find((l) => l.level === level)?.entries ?? [];
	const mine = entries.find((e) => e.memberId === board.me);
	if (!mine || mine.rank === null || !TIMED.has(mine.state)) return null;
	const timed = entries.filter((e) => e.rank !== null && TIMED.has(e.state));
	const lv = LEVEL_LABEL[level];
	if (timed.length === 1) {
		return { text: `First to finish in ${nestName} today`, spoken: `You’re the first in ${nestName} to finish today’s ${lv}. Open the nest.` };
	}
	const tie = timed.some((e) => e.memberId !== board.me && e.rank === mine.rank);
	const place = `${tie ? 'Joint ' : ''}${ordinal(mine.rank)}`;
	return {
		text: `${place} of ${timed.length} in ${nestName} today`,
		spoken: `You’re ${tie ? 'joint ' : ''}${ordinal(mine.rank)} of ${timed.length} in ${nestName} on today’s ${lv}. Open the nest.`,
	};
}

// --------------------------------------------------------------------------- invites

/** The invite link: the site's `/n/:code`, tagged with who sent it so the landing names them. */
export function inviteLink(nest: Pick<NestView, 'code' | 'me'>, base: string = SITE_URL): string {
	return `${base}/n/${encodeURIComponent(nest.code)}?by=${encodeURIComponent(nest.me.memberId)}`;
}

/** What the share sheet carries with the link. */
export function inviteText(nest: Pick<NestView, 'name' | 'code' | 'me'>, base: string = SITE_URL): string {
	return `Join ${nest.name} on Sparrow, a daily Arrowword: ${inviteLink(nest, base)}`;
}

/** "Jo and Dad have joined" (the invite page): first names, the first three, then a count. */
export function joinedLine(names: readonly string[], count: number = names.length): string {
	const shown = names.slice(0, 3);
	const more = Math.max(0, count - shown.length);
	if (shown.length === 0) return '';
	if (more > 0) return `${shown.join(', ')} and ${more} more have joined`;
	if (shown.length === 1) return `${shown[0]} has joined`;
	return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]} have joined`;
}

/** "Jo, Mum and Dad are in Family." (the Join sheet): first names, the first three, then a count. */
export function inLine(names: readonly string[], count: number, nestName: string): string {
	const shown = names.slice(0, 3);
	const more = Math.max(0, count - shown.length);
	if (shown.length === 0) return '';
	if (more > 0) return `${shown.join(', ')} and ${more} more are in ${nestName}.`;
	if (shown.length === 1) return `${shown[0]} is in ${nestName}.`;
	return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]} are in ${nestName}.`;
}

/**
 * Where joining from an invite lands (NJ-8): today's puzzle at the level you last played,
 * else Easy, else the day's first, as long as it isn't solved yet; if every level today is
 * solved, the Nest tab, where the people who invited you are.
 */
export function afterJoinStop(available: readonly Level[], remembered: Level | null, solved: (level: Level) => boolean): { to: 'daily'; level: Level } | { to: 'nest' } {
	const order = [remembered, 'easy' as const, ...available].filter((l): l is Level => !!l && available.includes(l));
	const level = order.find((l) => !solved(l));
	return level ? { to: 'daily', level } : { to: 'nest' };
}

/** "5 people" / "Just you": the switcher's line for each nest. */
export function peopleLine(count: number): string {
	return count <= 1 ? 'Just you' : `${count} people`;
}
